import React from 'react';
import { FormGroup, Label, Input, Row, Col, FormFeedback } from 'reactstrap';
import DatePicker from 'react-datepicker';

interface FormData {
  name: string;
  date: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  reasonForVerbalWarning: string;
  nextConsequences: string;
  expectationOfImprovement: string;
  privateNotes: string;
}

interface FormErrors {
  name?: string;
  date?: string;
  policyViolationIds?: string;
  reasonForVerbalWarning?: string;
  nextConsequences?: string;
  expectationOfImprovement?: string;
}

interface PolicyViolation {
  id: string;
  name: string;
}

interface UserInfoSectionProps {
  formData: FormData;
  errors: FormErrors;
  onChange: (field: keyof FormData, value: string) => void;
}

export const UserInfoSection = ({ formData, errors, onChange }: UserInfoSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Employee Information</h5>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="name">
            Name <span className="text-danger">*</span>
          </Label>
          <Input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            invalid={!!errors.name}
            readOnly
            disabled
          />
          {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="date">
            Date <span className="text-danger">*</span>
          </Label>
          <Input
            tag={DatePicker}
            dateFormat="MM/dd/yyyy"
            id="date"
            selected={formData.date ? new Date(formData.date + 'T00:00:00') : null}
            onChange={(d: any) => onChange('date', d ? d.toISOString().split('T')[0] : '')}
            maxDate={new Date()}
            placeholderText="Select date"
            autoComplete="off"
          />
          {errors.date && <FormFeedback>{errors.date}</FormFeedback>}
        </FormGroup>
      </Col>
    </Row>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="position">Position</Label>
          <Input
            type="text"
            id="position"
            value={formData.position}
            onChange={(e) => onChange('position', e.target.value)}
            readOnly
            disabled
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="department">Department</Label>
          <Input
            type="text"
            id="department"
            value={formData.department}
            onChange={(e) => onChange('department', e.target.value)}
            readOnly
            disabled
          />
        </FormGroup>
      </Col>
    </Row>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="supervisor">Supervisor</Label>
          <Input
            type="text"
            id="supervisor"
            value={formData.supervisor}
            readOnly
            onChange={(e) => onChange('supervisor', e.target.value)}
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="dateOfHire">Date of Hire (Read-only)</Label>
          <Input type="date" id="dateOfHire" value={formData.dateOfHire} readOnly disabled />
        </FormGroup>
      </Col>
    </Row>
  </div>
);

interface PolicySectionProps {
  formData: FormData;
  errors: FormErrors;
  policyViolations: PolicyViolation[];
  onChange: (field: keyof FormData, value: string | string[]) => void;
}

export const PolicySection = ({ formData, errors, policyViolations, onChange }: PolicySectionProps) => {
  const handleCheckboxChange = (violationId: string) => {
    const newIds = formData.policyViolationIds.includes(violationId)
      ? formData.policyViolationIds.filter((id) => id !== violationId)
      : [...formData.policyViolationIds, violationId];
    onChange('policyViolationIds', newIds);
  };

  return (
    <div className="mb-4">
      <h5 className="text-primary mb-3">Policy Violations</h5>
      <FormGroup>
        <Label>
          Policy Violations <span className="text-danger">*</span>
        </Label>
        <div>
          {policyViolations.map((violation) => (
            <FormGroup check key={violation.id} className="mb-2">
              <Label check>
                <Input
                  type="checkbox"
                  checked={formData.policyViolationIds.includes(violation.id)}
                  onChange={() => handleCheckboxChange(violation.id)}
                />
                {violation.name}
              </Label>
            </FormGroup>
          ))}
        </div>
        {errors.policyViolationIds && (
          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {errors.policyViolationIds}
          </div>
        )}
      </FormGroup>
    </div>
  );
};

interface ReasonForVerbalWarningSectionProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export const ReasonForVerbalWarningSection = ({ value, error, onChange }: ReasonForVerbalWarningSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Reason for Verbal Warning</h5>
    <FormGroup>
      <Label for="reasonForVerbalWarning">
        Description <span className="text-danger">*</span>
      </Label>
      <Input
        type="textarea"
        id="reasonForVerbalWarning"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={!!error}
        placeholder="Description of the issue or behavior that led to the verbal warning"
      />
      <small className="form-text text-muted">
        Description of the issue or behavior that led to the verbal warning
      </small>
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </div>
);

interface NextConsequencesSectionProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export const NextConsequencesSection = ({ value, error, onChange }: NextConsequencesSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Next Consequences</h5>
    <FormGroup>
      <Label for="nextConsequences">
        Description <span className="text-danger">*</span>
      </Label>
      <Input
        type="textarea"
        id="nextConsequences"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={!!error}
        placeholder="List out what consequences were communicated if this is to happen again"
      />
      <small className="form-text text-muted">
        List out what consequences were communicated if this is to happen again. Communicate – if violated again, it
        could lead to a written warning, suspension and/or termination.
      </small>
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </div>
);

interface ExpectationOfImprovementSectionProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export const ExpectationOfImprovementSection = ({ value, error, onChange }: ExpectationOfImprovementSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Expectation of Improvement(s)</h5>
    <FormGroup>
      <Label for="expectationOfImprovement">
        Description <span className="text-danger">*</span>
      </Label>
      <Input
        type="textarea"
        id="expectationOfImprovement"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={!!error}
        placeholder="Set realistic timelines for achieving these objectives"
      />
      <small className="form-text text-muted">
        Set realistic timelines for achieving these objectives. Include regular check-ins if necessary
      </small>
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </div>
);

interface PrivateNotesSectionProps {
  value: string;
  onChange: (value: string) => void;
}

export const PrivateNotesSection = ({ value, onChange }: PrivateNotesSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Private Notes</h5>
    <FormGroup>
      <Label for="privateNotes">Private Notes</Label>
      <Input
        type="textarea"
        id="privateNotes"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Private notes only seen by Director and Upper Management"
      />
      <small className="form-text text-muted">Only seen by Director and Upper Management</small>
    </FormGroup>
  </div>
);
